import React from "react";
import { motion } from "framer-motion";
import { Sparkles, BookOpenText } from "lucide-react";

// AMC inNOVAtions brand logo, drawn in SVG so it can inherit theme colours.
// A stylised triangle with an "eye" (neural core) and circuitry accents.
export function AmcLogo({ size = 44, glow = true }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 64 64"
      fill="none"
      style={glow ? { filter: "drop-shadow(0 0 10px rgba(0,240,255,0.55))" } : undefined}
      data-testid="amc-logo"
    >
      <defs>
        <linearGradient id="amcTri" x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#00F0FF" />
          <stop offset="100%" stopColor="#8B5CF6" />
        </linearGradient>
        <radialGradient id="amcEye" cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor="#FF3C7E" />
          <stop offset="60%" stopColor="#8B5CF6" />
          <stop offset="100%" stopColor="#00F0FF" />
        </radialGradient>
      </defs>
      <path d="M32 6 L58 54 L6 54 Z" stroke="url(#amcTri)" strokeWidth="2.4" />
      {/* Circuit lines inside triangle */}
      <path d="M18 46 L26 38 L38 38 L46 46" stroke="#00F0FF" strokeOpacity="0.55" strokeWidth="1" fill="none" />
      <path d="M32 20 L32 32" stroke="#00F0FF" strokeOpacity="0.7" strokeWidth="1" />
      <circle cx="32" cy="36" r="7" fill="url(#amcEye)" />
      <circle cx="32" cy="36" r="2" fill="#0a0a0a" />
      {/* Little sparks */}
      <circle cx="18" cy="46" r="1.4" fill="#00F0FF" />
      <circle cx="46" cy="46" r="1.4" fill="#FF3C7E" />
      <circle cx="32" cy="20" r="1.4" fill="#8B5CF6" />
    </svg>
  );
}

export default function BookCover({ onOpen }) {
  return (
    <div
      className="relative min-h-[calc(100vh-96px)] flex items-center justify-center px-6 py-16"
      data-testid="book-cover"
    >
      <motion.div
        initial={{ opacity: 0, y: 30, scale: 0.98 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.9, ease: "easeOut" }}
        className="book-page ring-neon max-w-3xl w-full px-8 sm:px-14 py-14 sm:py-20 text-center relative overflow-hidden"
      >
        {/* Corner circuit ornaments */}
        <div className="absolute -top-10 -left-10 w-40 h-40 opacity-40">
          <svg viewBox="0 0 160 160" width="100%" height="100%">
            <path d="M0,40 L60,40 L80,60 L160,60" stroke="#00F0FF" strokeWidth="1" fill="none" opacity="0.6" />
            <path d="M0,100 L40,100 L60,120 L160,120" stroke="#8B5CF6" strokeWidth="1" fill="none" opacity="0.5" />
            <circle cx="60" cy="40" r="2.5" fill="#00F0FF" />
            <circle cx="80" cy="60" r="2.5" fill="#00F0FF" />
          </svg>
        </div>
        <div className="absolute -bottom-10 -right-10 w-40 h-40 opacity-40 rotate-180">
          <svg viewBox="0 0 160 160" width="100%" height="100%">
            <path d="M0,40 L60,40 L80,60 L160,60" stroke="#FF3C7E" strokeWidth="1" fill="none" opacity="0.6" />
            <path d="M0,100 L40,100 L60,120 L160,120" stroke="#00F0FF" strokeWidth="1" fill="none" opacity="0.5" />
            <circle cx="60" cy="40" r="2.5" fill="#FF3C7E" />
            <circle cx="80" cy="60" r="2.5" fill="#00F0FF" />
          </svg>
        </div>

        <div className="flex justify-center mb-10">
          <img
            src="https://customer-assets.emergentagent.com/job_ai-app-manual/artifacts/2n601z17_edit_20260617_135003_643ee981.png"
            alt="AMC inNOVAtions — All-seeing eye pyramid with circuitry"
            data-testid="cover-pyramid-illustration"
            className="w-full max-w-xl h-auto select-none pointer-events-none"
            style={{ filter: "drop-shadow(0 0 40px rgba(0,240,255,0.35)) drop-shadow(0 0 80px rgba(255,60,126,0.15))" }}
          />
        </div>

        <div className="text-xs sm:text-sm uppercase tracking-[0.4em] text-cyan-300/80 mb-6 font-heading">
          The Quick Field Manual
        </div>

        <h1
          className="font-display text-5xl sm:text-6xl lg:text-7xl font-extrabold leading-[1.02] text-white mb-4"
          data-testid="book-title"
        >
          AMC&apos;S <span className="neon-cyan">ABC&apos;S</span>
          <br />
          of <span className="neon-magenta italic">AI</span>
        </h1>

        <div className="font-heading text-sm sm:text-base uppercase tracking-[0.3em] text-slate-400 mb-10">
          From AMC inNOVAtions
        </div>

        <p className="font-body max-w-xl mx-auto text-slate-300 text-base sm:text-lg leading-relaxed mb-12">
          A plain-English guide to building apps with AI — for humans who have never written a line of code. No jargon. No gatekeeping. Just how it actually works.
        </p>

        <motion.button
          whileHover={{ scale: 1.03, boxShadow: "0 0 30px rgba(0,240,255,0.45)" }}
          whileTap={{ scale: 0.98 }}
          onClick={onOpen}
          data-testid="cover-open-book-btn"
          className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-gradient-to-r from-cyan-400 to-violet-500 text-slate-950 font-heading font-semibold tracking-wide text-base sm:text-lg transition-all"
        >
          <BookOpenText size={20} />
          Open the Book
          <Sparkles size={18} />
        </motion.button>

        <div className="mt-14 flex items-center justify-center gap-6 text-slate-500 text-xs uppercase tracking-widest font-heading">
          <span>First Edition</span>
          <span className="w-1 h-1 rounded-full bg-cyan-400/60" />
          <span>{new Date().getFullYear()}</span>
          <span className="w-1 h-1 rounded-full bg-cyan-400/60" />
          <span>20 Chapters</span>
        </div>

        <a
          href="https://amcinnovate-tfnn9y4c.manus.space"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="cover-other-products-link"
          className="mt-8 inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.28em] text-slate-500 hover:text-cyan-200 font-heading border-t border-cyan-400/10 pt-6"
        >
          Explore other products by AMC inNOVAtions →
        </a>
      </motion.div>
    </div>
  );
}
